import { computed, DestroyRef, effect, inject, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  from,
  interval,
  iif,
  Observable,
  of,
  skip,
  Subject,
  switchMap,
  takeUntil,
  tap,
  filter,
  finalize,
  map,
  distinctUntilChanged,
  take,
} from 'rxjs';
import { tapResponse } from '@ngrx/operators';
// IosAsAMicListeningService is the last old-stack dependency here: voice-input has no iOS *pairing* API yet.
import { IosAsAMicListeningService, SpeechRecognitionStatus } from '@libs/speech-recognition';
import { AudioInputSelectorService, DictationSink, VoiceInputType, createVoiceChannel } from '@libs/voice-input';
import { FormControl } from '@angular/forms';
import { iosMicActions } from '@libs/ios-mic';
import { ConfigService, TagEvent, TagPusherService, ToastService, TokenService } from '@libs/shared-services';
import { TranslateService } from '@ngx-translate/core';
import {
  ClinicalNotesDataService,
  ClinicalNoteV2,
  ProvisionalStatus,
  AutodraftsDataService,
  Autodraft,
  DeepgramFeature,
  ErrorCode,
} from '@ffd/api-client';
import { Dialog } from '@angular/cdk/dialog';
import { ConfirmDialogComponent, ConfirmDialogResult, TemplateAutofillService } from '@libs/ui-lib';
import {
  selectUserSiteId,
  selectSelectedPatient,
  selectIsUserCollabOnlyAtSite,
  selectCurrentUserProfile,
  selectUserSite,
} from '@libs/store';
import { ApiHttpErrorResponse, routes, DataState, DISABLE_GLOBAL_ERROR_TOASTS } from '@libs/utils';
import { ComponentStore } from '@ngrx/component-store';
import { SchedulesService } from '@libs/schedule';
import { HttpContext } from '@angular/common/http';
import { DateTime } from 'luxon';
import { NotesScreenService } from '../components/notes-screen/notes-screen.service';
import { VisitWithAdditionalFlags } from '../components/notes-screen/notes-screen.interface';
import { AutoDraftGatewayService } from '../gateway/autodraft-gateway.service';
import { findTodayVisit, fromReadableStream } from '../components/notes-screen/notes-utils';
import { AutoDraftState } from './auto-draft-model';
import { AutoDraftCoordinatorService } from './auto-draft-coordinator.service';
import { SHOWCASE_AUTODRAFT_ID, soapKeywords } from './auto-draft.constants';
import { ShowcaseAutodraftService } from './showcase-autodraft.service';

const ENHANCEMENT_INTERVAL_MS = 60_000;
const APPOINTMENT_START_WINDOW_MINUTES = 30;
const APPOINTMENT_INDICATOR_TIMEOUT_MS = 60_000;
const ENHANCING_TIMEOUT_MS = 2000;
const HTTP_CONFLICT = 409;
const NEW_VISIT_ID = 'new-visit-id';

interface AutoDraftServiceState {
  lastAutodraftPatientId: string | undefined;
  lastAutodraftSiteId: string | undefined;
}

export class AutoDraftService extends ComponentStore<AutoDraftServiceState> {
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly configService = inject(ConfigService);
  private readonly tokenService = inject(TokenService);
  private readonly ngZone = inject(NgZone);
  private readonly translateService = inject(TranslateService);
  private readonly clinicalNotesDataService = inject(ClinicalNotesDataService);
  private readonly autoDraftDataService = inject(AutodraftsDataService);
  private readonly notesScreenService = inject(NotesScreenService);
  private readonly iosMicListeningService = inject(IosAsAMicListeningService);
  private readonly schedulesService = inject(SchedulesService);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly autoDraftGatewayService = inject(AutoDraftGatewayService);
  private readonly coordinator = inject(AutoDraftCoordinatorService);
  private readonly showcaseAutodraftService = inject(ShowcaseAutodraftService);
  private readonly autofillService = inject(TemplateAutofillService);
  private readonly audioInputSelector = inject(AudioInputSelectorService);
  private readonly voiceChannel = createVoiceChannel(VoiceInputType.AutoDraft, DeepgramFeature.AUTODRAFT_VOICE);

  /** Set while this service is the one closing the channel, so the watcher below can tell an external stop apart. */
  private stopRequestedByUs = false;
  private sessionWasActive = false;

  private abortController = new AbortController();
  private readonly stopEnhancement$ = new Subject<void>();
  private readonly isEnhancing$ = new BehaviorSubject<boolean>(false);

  private enhancedTextAccumulator: string = '';
  private appointmentIndicatorTimeout?: number;
  private enhancingTimeout?: number;
  private readonly appointmentIndicatorVisible = signal(true);
  private reopenOverlayAfterPatientSwitch = false;
  private openOverlayAfterMinimisedStop = false;

  /**
   * Live recording status of the AutoDraft session. It reads the voice channel directly instead of the
   * coordinator's mirror, because the mirror is written by an effect and therefore lags one flush behind:
   * close() also writes the final transcript into the coordinator synchronously, which re-runs the
   * state-sync effect below in that same flush. With the mirrored status that re-run would still see
   * 'recording', derive InProgress, and wipe the just-set 'pause' intent - leaving the overlay in
   * Enhancing ("Updating") instead of Paused.
   * The channel is AutoDraft's own, so no "do I own the session?" gate is needed.
   */
  public readonly voiceTypingStatus = computed<SpeechRecognitionStatus>(() => {
    if (this.voiceChannel.isLoading()) {
      return 'loading';
    }

    return this.voiceChannel.isListening() ? 'recording' : 'idle';
  });

  // Reactive selectors for state properties
  private readonly isEnhancing = toSignal(this.isEnhancing$, { requireSync: true });
  public readonly userId = computed(() => this.store.selectSignal(selectCurrentUserProfile)()?.id);
  /**
   * Derived auto-draft state:
   * - Loading while initializing websocket connection
   * - InProgress while actively recording
   * - Enhancing while waiting for final transcriptions and SOAP generation (after pause or stop is clicked)
   * - Paused if we have saved transcription and user paused (reached via pause button)
   * - Stopped if we have saved transcription and user stopped (reached via stop button)
   * - Inactive otherwise
   */
  public readonly autoDraftState = computed<AutoDraftState>(() => {
    const status = this.voiceTypingStatus();
    const stopIntent = this.coordinator.stopIntent();

    // Error: SOAP generation failed (check first so it wins over every other branch)
    if (this.coordinator.hasGenerationError()) {
      return AutoDraftState.Error;
    }

    // Enhancing: when user clicked pause or stop (check this first!)
    // Pausing never enters Enhancing — it must never show the "Updating" spinner.
    if (this.isEnhancing() && stopIntent !== 'pause') {
      return AutoDraftState.Enhancing;
    }

    // Starting: initializing websocket
    if (status === 'loading') {
      return AutoDraftState.Loading;
    }

    // Recording: actively recording (takes priority over enhancement loading)
    if (status === 'recording') {
      return AutoDraftState.InProgress;
    }

    // Enhancing: if enhancing while not recording (final SOAP generation after pause/stop)
    if (this.coordinator.showEnhanceLoading() && stopIntent !== 'pause') {
      return AutoDraftState.Enhancing;
    }

    if (stopIntent === 'stop') {
      return AutoDraftState.Stopped;
    }
    if (stopIntent === 'pause') {
      return AutoDraftState.Paused;
    }

    return AutoDraftState.Inactive;
  });

  public readonly voiceTypingSessionId = computed(() => this.coordinator.voiceTypingSessionId());
  public readonly iosDevices = this.coordinator.iosDevices;
  public readonly webDeviceName = this.coordinator.webDeviceName;
  /** Owned rather than borrowed from a provider; the sink below writes what the channel hears into it. */
  private readonly ownLiveTranscriptionControl = new FormControl<string | null>('');
  /**
   * Subscribes before the gateway push below, so the push always reads text that already includes the utterance
   * that triggered it. Hypotheses reach the live bubble as they arrive, which is what the shared provider control
   * used to do for auto-draft; only finals reach the gateway, because only the push is driven by transcript$.
   */
  private readonly dictation = new DictationSink(this.voiceChannel, this.ownLiveTranscriptionControl);
  public readonly liveTranscriptionControl = computed(() => this.ownLiveTranscriptionControl);

  private readonly siteId = computed(() => this.store.selectSignal(selectUserSiteId)());
  private readonly lastAutodraftSiteId = this.selectSignal((s) => s.lastAutodraftSiteId);
  private readonly lastAutodraftPatientId = this.selectSignal((s) => s.lastAutodraftPatientId);
  private readonly patientId = computed(() => this.store.selectSignal(selectSelectedPatient)()?.data?.id ?? this.lastAutodraftPatientId());
  private readonly noteProviderForNewNote = computed(() => this.notesScreenService.state().noteProviderForNewNote);
  private readonly activeAutodraftId = computed(() => this.coordinator.activeAutodraft().id);
  private readonly activeAutodraftId$ = toObservable(this.activeAutodraftId);
  private readonly showEnhanceLoading$ = toObservable(this.coordinator.showEnhanceLoading);
  private readonly autoDraftState$ = toObservable(this.autoDraftState);

  private readonly isShowcaseSite = computed(() => this.showcaseAutodraftService.isShowcaseSite(this.lastAutodraftSiteId() ?? undefined));

  private readonly isAutodraftResolved = computed(() => {
    const patientId = this.patientId();
    const notesState = this.notesScreenService.state();
    const areVisitsPending = notesState.patientId === patientId && notesState.listStatus !== DataState.Fetched;

    return !!patientId && !areVisitsPending && !this.coordinator.isAutodraftDataLoading();
  });

  constructor() {
    super({
      lastAutodraftPatientId: undefined,
      lastAutodraftSiteId: undefined,
    });

    this.store
      .select(selectIsUserCollabOnlyAtSite)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((isCollabOnly) => {
        if (isCollabOnly !== undefined && !isCollabOnly) {
          this.schedulesService.init();
        }
      });
    // AutoDraftService is provided in the navbar, which mounts before the routed notes screen
    // (the component that normally dispatches this action), so the iOS mic store would otherwise
    // still be empty here, causing initializeLastUsedDevice() to fall back to the default mic.
    this.store.dispatch(iosMicActions.initialize());
    this.iosMicListeningService.startListeningForDevices();
    this.liveTranscriptionControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((text) => {
        if (text) {
          this.coordinator.setLiveTranscriptionText(text);
        }
      });

    // Stop recording and reset state when switching patients
    this.store
      .select(selectSelectedPatient)
      .pipe(
        map((selectedPatient) => selectedPatient?.data?.id),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((newPatientId) => {
        const currentStoredPatientId = this.state().lastAutodraftPatientId;

        // If we have a new defined patient ID that differs from what we have stored, reset
        if (newPatientId && currentStoredPatientId && newPatientId !== currentStoredPatientId && newPatientId !== undefined) {
          this.closeAutodraftSessionAndModal();

          if (this.isShowcaseSite()) {
            const autodraftId = this.showcaseAutodraftService.getShowcaseAutodraftId(newPatientId);
            const siteId = this.lastAutodraftSiteId();
            if (autodraftId && siteId) {
              this.showcaseAutodraftService.syncAutodraftIdForPatient(newPatientId);
              this.coordinator.setActiveAutodraftId(autodraftId);
              this.getAutoDraftData({ siteId, autoDraftId: autodraftId, target: 'active' });
            }
          }
        }
      });

    // Stop recording and reset state when switching sites
    let previousSiteId: string | undefined;
    this.store
      .select(selectUserSite)
      .pipe(
        map((selectedSite) => selectedSite?.id),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((newSiteId) => {
        if (previousSiteId && newSiteId && previousSiteId !== newSiteId) {
          this.closeAutodraftSessionAndModal();
        }
        previousSiteId = newSiteId;
      });

    // Sync autodraft state to coordinator and manage periodic enhancement
    effect(() => {
      const state = this.autoDraftState();
      this.coordinator.setAutoDraftState(state);
      this.autofillService.setAutodraftVisitId(this.coordinator.activeAutodraft().visitId);
      this.autofillService.setAutoDraftVoiceState({
        inProgress: state === AutoDraftState.InProgress,
        loading: state === AutoDraftState.Loading,
      });

      if (state === AutoDraftState.InProgress) {
        // Clear stopIntent only once recording is truly live, so the resume-Loading window
        // still reports stopIntent === 'pause' and is rendered like the paused state, not a fresh start.
        this.coordinator.setStopIntent(null);
        this.startPeriodicEnhancement();
      } else if (state === AutoDraftState.Enhancing && this.isEnhancing()) {
        // Stop periodic enhancement when we first enter enhancing state (waiting for final transcriptions)
        this.stopPeriodicEnhancement();
      } else if ((state === AutoDraftState.Paused || state === AutoDraftState.Inactive) && !this.coordinator.showEnhanceLoading()) {
        // Clean up periodic enhancement for final states
        this.stopPeriodicEnhancement();
      }
    });

    effect(() => {
      if (this.autoDraftState() === AutoDraftState.Stopped && this.openOverlayAfterMinimisedStop) {
        this.openOverlayAfterMinimisedStop = false;
        this.coordinator.openOverlay();
      }
    });

    effect(() => {
      const nextPatientId = this.patientId();
      if (nextPatientId) {
        this.patchState({ lastAutodraftPatientId: nextPatientId });
      }
    });

    // Expose the running autodraft's patient so AutoStop prompts keep working after the user
    // navigates away from the patient context page (FW10-3928). Scoped to the active session and
    // cleared otherwise, so a stale patient can't linger and fire prompts for the wrong patient.
    effect(() => {
      const isRunning = this.autoDraftState() === AutoDraftState.InProgress;
      this.coordinator.setAutodraftPatientId(isRunning ? this.patientId() : undefined);
    });

    effect(() => {
      this.coordinator.setAutodraftResolved(this.isAutodraftResolved());
    });

    effect(() => {
      const nextSiteId = this.siteId();
      if (nextSiteId) {
        this.patchState({ lastAutodraftSiteId: nextSiteId });
      }
    });

    // Mirrors the live channel status onto the coordinator for consumers that read it from there. Derived from
    // voiceTypingStatus so the mirror can never drift from what this service itself acts on. The channel is
    // AutoDraft's own, so no "do I own the session?" gate is needed any more — that guard only existed because
    // ActiveTranscriptionService was shared with Notes Voice Typing.
    effect(() => {
      this.coordinator.setVoiceTypingStatus(this.voiceTypingStatus());
    });

    effect(() => {
      this.coordinator.setActiveDeviceId(this.audioInputSelector.selectedDevice()?.deviceId ?? '');
    });

    // Handle microphone selection forwarded from the coordinator
    this.coordinator.commandSelectMicrophone$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((deviceId) => {
      this.audioInputSelector.selectDevice(deviceId);
    });

    // commandSetDefaultDevice$ needs no handler: the hub stores the chosen id and re-resolves it, so a device that
    // goes away falls back to the platform default without anyone choosing.

    // Automatically load today's autodraft when visits are loaded (e.g. after patient switch)
    // This ensures the button state updates without needing to click the button.
    // The patient is a trigger too, not only the visits: creating an appointment already loads that patient's visits, so
    // opening the patient afterwards does not reload them. With the visits alone as the trigger, the load never ran
    // after the patient switch, the autodraft loading flag the switch sets stayed set, and the Start appointment prompt
    // never showed (FW10-4291). toObservable emits after the synchronous patient-switch reset above, so the load runs last.
    combineLatest([
      toObservable(this.patientId).pipe(distinctUntilChanged()),
      this.notesScreenService.state$.pipe(
        map((state) => state.visits),
        distinctUntilChanged(),
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([, visits]) => {
        const activeAutodraftId = this.coordinator.activeAutodraft().id;
        const isReadOnly = this.coordinator.isReadOnly();
        const currentPatientId = this.patientId();

        // Only try to load if we don't already have one and not in read-only mode
        // Also ensure we have a valid patient context before attempting to load.
        // An empty visit list must resolve too: a patient switch leaves the autodraft loading flag set until this load
        // runs, so a patient without visits kept it set, and the Start appointment prompt never showed (FW10-4291).
        if (!activeAutodraftId && !isReadOnly && currentPatientId) {
          // For showcase: if the fresh visits have no autodraftId yet but we already hold
          // one in the in-memory map, write it back onto the visit and let the resulting
          // state emission re-trigger this subscription with the id already present.
          if (
            this.isShowcaseSite() &&
            visits.length > 0 &&
            !findTodayVisit(visits, this.notesScreenService.timezoneSignal() as string)?.autodraftId
          ) {
            this.showcaseAutodraftService.syncAutodraftIdForPatient(currentPatientId);
            return;
          }
          this.tryLoadExistingAutodraft();
        }
      });

    // transcript$ emits finals only, so every event here is an end-of-sentence.
    this.voiceChannel.transcript$
      .pipe(
        filter(() => this.autoDraftGatewayService.isJoined()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const transcriptionText = this.coordinator.transcriptionText();

        const liveTranscriptionText = this.coordinator.liveTranscriptionText();
        const fullText = transcriptionText ? `${transcriptionText} ${liveTranscriptionText}` : liveTranscriptionText;

        // Autofill is no longer fed from here: it opens its own channel alongside this one, so pushing our
        // transcription across would trigger it twice for the same speech.

        // Push only to the draft that the gateway is connected to: a push to another draft only fails into an error toast.
        const autodraftId = this.coordinator.activeAutodraft().id;
        if (fullText.trim() && autodraftId && autodraftId === this.autoDraftGatewayService.getCurrentAutodraftId()) {
          this.autoDraftGatewayService.updateTranscription(fullText.trim(), autodraftId);
        }
      });

    this.autoDraftGatewayService.autodraftException$.pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
      console.error('Autodraft websocket exception:', payload);
      this.toastService.error(`Autodraft error: ${payload.message}`);
    });

    // Subscribe to final SOAP generated event to reload data
    this.autoDraftGatewayService.finalSoapGenerated$
      .pipe(
        filter((payload) => !!payload),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => {
        if (payload) {
          const siteId = this.lastAutodraftSiteId();
          if (siteId) {
            // Reload the autodraft data with the generated SOAP
            this.getAutoDraftData({ siteId, autoDraftId: payload.autodraftId, target: 'active' });
          }
        }
      });

    this.coordinator.requestInit$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.init();
    });

    this.coordinator.requestResetReadOnlyMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.resetReadOnlyMode();
    });

    this.coordinator.requestLoadAutodraft$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((request) => {
      this.loadAutoDraftById(request.autodraftId, request.isReadOnly, request.isDrawerStyle);
    });

    // ── Overlay command bus ───────────────────────────────────────────────────
    this.coordinator.commandStartVoiceTyping$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAutoStart: boolean | void) => this.startVoiceTyping(isAutoStart ?? false));
    this.coordinator.commandPauseVoiceTyping$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.coordinator.setStopIntent('pause');
      this.stopVoiceTyping();
    });
    this.coordinator.commandStopVoiceTyping$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.openOverlayAfterMinimisedStop = !this.coordinator.isOverlayOpen();
      this.coordinator.setStopIntent('stop');

      if (this.isRecordingActive()) {
        this.stopVoiceTyping();
      } else {
        // A paused draft has no recording to close, and stopVoiceTyping() returns early without one, so stopping it
        // generated nothing (FW10-4324). Only the final note is missing, from the transcript saved at the pause.
        // A refresh the pause still has pending is dropped, so it cannot restart the generation under this stop.
        this.clearEnhancingTimeout();
        this.generateFinalNote();
      }
    });
    this.coordinator.commandStopAndSaveNote$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.stopAndSaveNote());
    // The active input (e.g. an iOS mic disconnecting) can end the session outside the command bus above.
    // Replaces recordingStoppedExternally$: watch our own channel go quiet and, when we did not ask for it,
    // run the same enhancing/finalization workflow as an explicit stop, so the popup does not get stuck
    // showing "Updating" until a page refresh.
    effect(() => {
      const isActive = this.voiceChannel.isListening() || this.voiceChannel.isLoading();

      if (isActive) {
        this.sessionWasActive = true;

        return;
      }

      if (!this.sessionWasActive) {
        return;
      }

      this.sessionWasActive = false;

      if (this.stopRequestedByUs) {
        this.stopRequestedByUs = false;

        return;
      }

      this.coordinator.setStopIntent('pause');
      // Keep what was heard: the next start clears the live buffer, so it has to be in the saved transcription by then.
      this.handleTranscriptionResult(this.coordinator.liveTranscriptionText());
      // The 'pause' intent above makes beginEnhancingAfterStop() arm the sweep itself, once its refresh starts.
      this.beginEnhancingAfterStop();
    });

    this.coordinator.commandTriggerNoteEnhancement$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.triggerNoteEnhancement());
    this.coordinator.commandOpenConfirmDeleteDialog$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.openConfirmDeleteDialog());
    this.coordinator.commandSaveNote$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((text) => this.saveNoteAsClinicalNote(text));
    this.coordinator.commandEnhanceAndSaveNote$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.enhanceAndSaveNote());
    this.coordinator.commandRefreshNoteThen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((done) => this.refreshNoteThen(done));
    this.coordinator.commandStartNew$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.openConfirmStartNewDialog());
    this.coordinator.commandResumeFromError$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.resumeFromError());
    effect(() => {
      this.coordinator.setShowAppointmentIndicator(this.appointmentIndicatorVisible() && this.hasCurrentOrUpcomingAppointment());
    });
  }

  private updateCurrentShowcaseAutodraft(update: { transcription?: string; noteText?: string; initiatorId?: string }): void {
    if (!this.isShowcaseSite()) {
      return;
    }

    this.showcaseAutodraftService.updateCurrentAutodraft(this.patientId(), update, this.userId() ?? '');
  }

  public init(): void {
    // Only clear live transcription if we don't have an autodraft loaded
    // This prevents clearing data when reopening an existing autodraft
    if (!this.coordinator.activeAutodraft().id) {
      this.coordinator.setLiveTranscriptionText('');
      this.liveTranscriptionControl().reset('', { emitEvent: false });
      this.coordinator.setAutodraftDataLoading(true);
    }

    // Try to load existing autodraft (don't create new one yet)
    this.tryLoadExistingAutodraft();
  }

  /**
   * Try to load today's existing autodraft without creating a new one.
   * If no autodraft exists, it will be created when recording starts.
   */
  private tryLoadExistingAutodraft(): void {
    // Don't reload from the backend while recording is active — the server's stored
    // transcription lags behind what's in liveTranscriptionText, so overwriting
    // coordinator.transcriptionText() with it would cause that segment to be
    // duplicated when building the final SOAP input on stop.
    if (this.voiceTypingStatus() === 'recording' || this.voiceTypingStatus() === 'loading') {
      return;
    }

    const siteId = this.lastAutodraftSiteId();
    const currentPatientId = this.patientId();
    const todayVisit = findTodayVisit(this.notesScreenService.state().visits, this.notesScreenService.timezoneSignal() as string);
    const autodraftId = this.isShowcaseSite()
      ? this.showcaseAutodraftService.getShowcaseAutodraftId(currentPatientId ?? '')
      : todayVisit?.autodraftId;

    if (!currentPatientId || todayVisit?.patientId !== currentPatientId) {
      const currentState = this.autoDraftState();
      if (currentState === AutoDraftState.Paused || currentState === AutoDraftState.Stopped) {
        return;
      }
      this.resetAutodraftState(todayVisit && this.coordinator.isAutodraftDataLoading());
      this.reopenOverlayAfterPatientSwitch = false;
      return;
    }

    // Only load if today's visit has an autodraft
    if (todayVisit?.visitDate && autodraftId && siteId) {
      this.coordinator.setActiveAutodraftId(autodraftId);
      this.getAutoDraftData({ siteId, autoDraftId: autodraftId, target: 'active' });
    } else {
      this.resetAutodraftState();
      this.reopenOverlayAfterPatientSwitch = false;
    }
    // If no autodraft for today's visit, do nothing - will be created when recording starts
  }

  /**
   * Load a specific autodraft by ID (used when clicking on visit autodraft button)
   * @param autodraftId The ID of the autodraft to load
   * @param isReadOnly Whether to open in read-only mode (disable recording)
   */
  public loadAutoDraftById(autodraftId: string, isReadOnly: boolean, isDrawerStyle: boolean): void {
    const siteId = this.lastAutodraftSiteId();
    if (siteId) {
      this.coordinator.setIsReadOnly(isReadOnly);
      this.coordinator.setIsDrawerStyle(isDrawerStyle);
      const target: 'active' | 'selected' = isReadOnly ? 'selected' : 'active';
      if (target === 'active') {
        this.coordinator.setActiveAutodraftId(autodraftId);
      } else {
        // Write a synchronous placeholder before the async fetch resolves, so displayedAutodraft()
        // (selectedAutodraft ?? activeAutodraft) reflects the historical draft from the first frame
        // instead of falling back to today's active draft while loading.
        this.coordinator.setSelectedAutodraft({ id: autodraftId, visitId: '', transcription: '', noteText: '' });
      }
      this.getAutoDraftData({ siteId, autoDraftId: autodraftId, target });
    }
  }

  /**
   * Reset to non-read-only mode (for creating new autodrafts)
   */
  public resetReadOnlyMode(): void {
    this.coordinator.setIsReadOnly(false);
    this.coordinator.setActiveAutodraftId();
    this.coordinator.clearSelectedAutodraft();
  }

  /**
   * Reset all autodraft state - used when switching patients to prevent data leakage.
   * @param showLoadingAfterReset When true, leaves isAutodraftDataLoading as true so the overlay
   * shows a loading indicator while the new patient's autodraft is being fetched.
   * @param closeOverlay When true, forcibly closes the overlay. Only pass true from patient/site
   * switch paths — never from tryLoadExistingAutodraft, which runs after the user may have already
   * intentionally reopened the overlay for the new patient.
   */
  private resetAutodraftState(showLoadingAfterReset = false, closeOverlay = false): void {
    // Clear coordinator state
    this.coordinator.clearActiveAutodraft();
    this.coordinator.setIsReadOnly(false);
    this.coordinator.setAutodraftDataLoading(showLoadingAfterReset);
    this.coordinator.setIsDrawerStyle(false);
    this.coordinator.setActiveAutodraftId();
    this.coordinator.setLiveTranscriptionText('');
    this.coordinator.setStopIntent(null);
    this.coordinator.setHasGenerationError(false);
    // The initiator belongs to the draft we just dropped, not to the patient we are switching to.
    // Without this the overlay header would keep showing the previous patient's initiator and the
    // navbar would keep hiding the resume/stop controls until some *other* draft happens to load.
    this.coordinator.clearInitiator();
    if (closeOverlay) {
      this.coordinator.setOverlayOpen(false);
    }

    // Clear the form control to prevent previous patient's transcription from persisting
    this.liveTranscriptionControl().reset('', { emitEvent: false });

    // Stop all ongoing operations and clear their state
    this.stopPeriodicEnhancement();
    this.clearEnhancingTimeout(); // Clears isEnhancing subject and timeout
  }

  private closeAutodraftSessionAndModal(): void {
    this.reopenOverlayAfterPatientSwitch = this.coordinator.isOverlayOpen();
    this.coordinator.requestCloseOverlay$.next();

    const isRecording = this.voiceTypingStatus() === 'recording' || this.voiceTypingStatus() === 'loading';
    if (isRecording) {
      // Opening another patient only pauses the draft (FW10-4324): the previous patient's draft is kept for a resume,
      // and no final note is generated for it.
      this.coordinator.setStopIntent('pause');
      this.stopVoiceTyping();
      this.isEnhancing$
        .pipe(
          filter((isEnhancing) => !isEnhancing),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.resetAutodraftState(true, true);
        });
      return;
    }

    this.resetAutodraftState(true, true);
  }

  public readonly loadAutoDraftFromBackend = this.effect(($: Observable<{ siteId: string; patientId: string }>) =>
    $.pipe(
      switchMap(({ siteId, patientId }) => {
        if (this.isShowcaseSite()) {
          this.showcaseAutodraftService.createCurrentAutodraft(this.patientId(), this.userId() ?? '');
          return EMPTY;
        }

        // Try to create a new autoDraft for today's visit
        return this.autoDraftDataService
          .createAutodraft(siteId, patientId, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              (response) => {
                this.getAutoDraftData({ siteId, autoDraftId: response.autodraftId, skipPausedFallback: true, target: 'active' });
                this.refetchVisitsAndSelect(response.visitId);
              },
              (error: { status?: number }) => {
                // AutoDraft already exists for today's visit
                if (error.status === HTTP_CONFLICT) {
                  const todayVisit = findTodayVisit(
                    this.notesScreenService.state().visits,
                    this.notesScreenService.timezoneSignal() as string,
                  );
                  const latestAutodraftId = todayVisit?.autodraftId;

                  this.getAutoDraftData({ siteId, autoDraftId: latestAutodraftId ?? '', skipPausedFallback: true, target: 'active' });
                  this.refetchVisitsAndSelect(todayVisit?.visitId ?? '');
                } else {
                  console.error('Error creating autoDraft:', error);
                  this.clearTranscriptionAndNote();
                }
              },
            ),
          );
      }),
    ),
  );

  public readonly getAutoDraftData = this.effect(
    (
      $: Observable<{
        siteId: string;
        autoDraftId: string;
        skipPausedFallback?: boolean;
        target: 'active' | 'selected';
      }>,
    ) =>
      $.pipe(
        tap(() => {
          this.coordinator.setAutodraftDataLoading(true);
        }),
        switchMap(({ siteId, autoDraftId, skipPausedFallback = false, target }) => {
          // Nothing is persisted for the showcase site, so its autodrafts must never be fetched from the
          // backend - the request would only 404 and surface a global "Not found" toast (FW10-4162). An id
          // without our in-memory prefix cannot exist here either, so it resolves to "no data" as well.
          if (this.isShowcaseSite()) {
            const showcaseAutodraft = autoDraftId.startsWith(SHOWCASE_AUTODRAFT_ID)
              ? this.showcaseAutodraftService.loadAutodraftToCoordinator(autoDraftId, this.patientId(), this.userId() ?? '')
              : undefined;

            if (!showcaseAutodraft) {
              this.clearTranscriptionAndNote();
              this.coordinator.setAutodraftDataLoading(false);
              return EMPTY;
            }

            this.coordinator.setInitiatorId(showcaseAutodraft.initiatorId);
            // Showcase drafts are local demo data always created by the current user, so there is
            // no initiator name to show; clear it so a previously loaded draft's name cannot linger.
            this.coordinator.setInitiatorName('');
            this.coordinator.setIsInitiatedByOther(showcaseAutodraft.initiatorId !== this.userId());
            this.coordinator.setAutodraftDataLoading(false);

            return EMPTY;
          }

          return this.autoDraftDataService.getAutodraft(siteId, autoDraftId).pipe(
            tapResponse(
              (autoDraft) => this.handleAutoDraftLoaded(autoDraft, autoDraftId, target, skipPausedFallback),
              (error: unknown) => this.handleAutoDraftLoadError(error),
            ),
          );
        }),
      ),
  );

  private handleAutoDraftLoaded(
    autoDraft: Autodraft,
    autoDraftId: string,
    target: 'active' | 'selected',
    skipPausedFallback: boolean,
  ): void {
    // CRITICAL: Validate that the loaded autodraft belongs to the current patient
    // This prevents displaying autodraft data from a different patient
    const currentPatientId = this.patientId();
    if (currentPatientId && autoDraft.patientId && autoDraft.patientId !== currentPatientId) {
      console.warn('Autodraft patient mismatch - ignoring loaded data', {
        autodraftPatientId: autoDraft.patientId,
        currentPatientId,
      });
      this.clearTranscriptionAndNote();
      this.coordinator.clearInitiator();
      this.coordinator.setAutodraftDataLoading(false);
      this.reopenOverlayAfterPatientSwitch = false;
      return;
    }

    const loadedData = {
      id: autoDraftId,
      visitId: autoDraft.visitId,
      transcription: autoDraft.transcription ?? '',
      noteText: autoDraft.soap ?? '',
    };

    if (target === 'selected') {
      this.coordinator.setSelectedAutodraft(loadedData);
    } else {
      this.coordinator.setActiveAutodraft(loadedData);
    }
    this.coordinator.setAutodraftDataLoading(false);
    this.coordinator.setShowEnhanceLoading(false);
    this.coordinator.setHasGenerationError(false);
    this.coordinator.setInitiatorId(autoDraft.initiatorId);
    this.coordinator.setInitiatorName(autoDraft.initiatorName);
    const isInitiatedByOther = autoDraft.initiatorId !== this.userId();
    this.coordinator.setIsInitiatedByOther(isInitiatedByOther);

    // An existing autodraft loaded from the server has no active recording,
    // so treat it as paused so it stays visible in the header. Skipped when this
    // load is part of creating and immediately starting a brand-new recording.
    // Also skipped for a draft another provider started: it is not this user's session to pause, and a paused
    // state would keep the header icon and the inactivity countdown alive for a draft they cannot control.
    if (!skipPausedFallback && !isInitiatedByOther && this.coordinator.stopIntent() === null) {
      this.coordinator.setStopIntent('pause');
    }

    if (this.reopenOverlayAfterPatientSwitch) {
      this.coordinator.setOverlayOpen(true);
    }
    this.reopenOverlayAfterPatientSwitch = false;
  }

  private handleAutoDraftLoadError(error: unknown): void {
    console.error('Error getting autoDraft data:', error);
    this.clearTranscriptionAndNote();
    this.coordinator.clearInitiator();
    this.coordinator.setAutodraftDataLoading(false);
    this.coordinator.setShowEnhanceLoading(false);
    this.coordinator.setActiveAutodraftId();
    this.reopenOverlayAfterPatientSwitch = false;
  }

  private hasCurrentOrUpcomingAppointment(): boolean {
    const patientId = this.patientId();
    const selectedOperatoryAppointments = this.schedulesService.viewModel().selectedOperatoryAppointments;

    if (!patientId || !selectedOperatoryAppointments?.length) {
      return false;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    return selectedOperatoryAppointments.some((operatory) =>
      operatory.appointments.some((appointment) => {
        if (appointment.patientId !== patientId) return false;

        const startHour = appointment.from.hour;
        const startMinute = appointment.from.minute;
        const endHour = appointment.to.hour;
        const endMinute = appointment.to.minute;

        // Convert to minutes for easier comparison
        const currentTotalMinutes = currentHour * 60 + currentMinute;
        const startTotalMinutes = startHour * 60 + startMinute;
        const endTotalMinutes = endHour * 60 + endMinute;

        // Check if current time is within appointment window OR appointment starts in next 30 minutes
        const isCurrentAppointment = currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes;
        const isStartingSoon =
          startTotalMinutes > currentTotalMinutes && startTotalMinutes <= currentTotalMinutes + APPOINTMENT_START_WINDOW_MINUTES;
        return isCurrentAppointment || isStartingSoon;
      }),
    );
  }

  private createClinicalNote(clinicalNoteId: string, date: Date, patientId: string, noteText: string): ClinicalNoteV2 {
    return {
      id: clinicalNoteId,
      entry: noteText,
      date: date.toISOString(),
      patientId,
      isLocked: false,
      isProvisional: false,
      provisionalStatus: ProvisionalStatus.READY,
    };
  }

  private updateSelectedVisit(selectedVisit: VisitWithAdditionalFlags, visits: VisitWithAdditionalFlags[], clinicalNote: ClinicalNoteV2) {
    const updatedVisit = {
      ...selectedVisit,
      clinicalNotes: [clinicalNote, ...(selectedVisit.clinicalNotes || [])],
    };

    const updatedVisits = visits.map((visit) => (visit.visitId === selectedVisit?.visitId ? updatedVisit : visit));
    this.notesScreenService.updateVisits(updatedVisits);
    this.notesScreenService.setActiveVisit({ visitId: selectedVisit.visitId });
  }

  private createNewVisit(date: Date, siteId: string, patientId: string, clinicalNote: ClinicalNoteV2) {
    const newVisit = {
      visitId: NEW_VISIT_ID,
      progressNotes: [],
      clinicalNotes: [clinicalNote],
      isAddedTreatmentGroup: true,
      siteId,
      patientId,
      visitDate: date.toISOString().split('T')[0], // toDateFormat equivalent
      createdDate: date.toISOString().split('T')[0],
      updatedDate: date.toISOString().split('T')[0],
    };

    const currentVisits = this.notesScreenService.state().visits;
    const addedTreatmentGroupIndex = currentVisits.findIndex((visit: VisitWithAdditionalFlags) => visit.isAddedTreatmentGroup);
    const hasAddedTreatmentGroup = addedTreatmentGroupIndex > -1;

    if (hasAddedTreatmentGroup) {
      const updatedVisits = [
        {
          ...newVisit,
          progressNotes: currentVisits[addedTreatmentGroupIndex].progressNotes,
        },
        ...currentVisits.filter((visit: VisitWithAdditionalFlags) => !visit.isAddedTreatmentGroup),
      ];
      this.notesScreenService.updateVisits(updatedVisits);
    } else {
      this.notesScreenService.updateVisits([newVisit, ...currentVisits]);
    }
    this.notesScreenService.setActiveVisit({ visitId: newVisit.visitId });
  }

  private refetchVisitsAndSelect(visitId: string): void {
    this.notesScreenService.state$
      .pipe(
        skip(1),
        filter((state) => state.listStatus === DataState.Fetched),
        take(1),
      )
      .subscribe(() => {
        this.notesScreenService.setActiveVisit({ visitId });
        setTimeout(() => {
          this.notesScreenService.noteListElementRefSignal()?.scrollToVisit(visitId);
        }, 100);
      });
    this.notesScreenService.getVisits({ shouldShowLoading: false });
  }

  private finalizeNoteSave(visitId: string | undefined, clinicalNoteId: string, patientId: string, siteId: string): void {
    this.notesScreenService.patchState({
      editingNoteId: clinicalNoteId,
      noteSavingStatus: DataState.Fetched,
      autoDraftSavedNoteId: clinicalNoteId,
    });

    if (visitId) {
      setTimeout(() => {
        this.notesScreenService.noteListElementRefSignal()?.scrollToVisit(visitId);
      }, 100);
    }

    this.coordinator.setIsSaving(false);
    this.handleSuccessActions(patientId, siteId, clinicalNoteId);
  }

  private handleSuccessActions(patientId: string, siteId: string, _createdClinicalNoteId: string): void {
    // The success toast now lives inside the Note Editor (anchored, with Undo), fired from EditNoteComponent
    // once the saved note opens.
    this.tagPusherService.pushTag({
      event: TagEvent.AutoDraftNoteCreated,
      patient_id: patientId,
      site_id: siteId,
    });

    if (siteId && patientId) {
      this.coordinator.requestCloseOverlay$.next();

      const currentUrl = this.router.url;
      if (!currentUrl.includes(routes.notes)) {
        // Redirecting away from Perio: flag it so Perio can reassure the user on return.
        if (currentUrl.includes(routes.perio)) {
          this.coordinator.setSavedDuringPerio(true);
        }
        this.router.navigate([routes.site, this.lastAutodraftSiteId(), routes.patients, this.patientId(), routes.notes]);
      }
    }
  }

  private startPeriodicEnhancement(): void {
    this.stopPeriodicEnhancement();
    this.coordinator.setShowEnhanceLoading(true);
    interval(ENHANCEMENT_INTERVAL_MS)
      .pipe(takeUntil(this.stopEnhancement$))
      .subscribe(() => {
        if (this.autoDraftState() === AutoDraftState.InProgress && !this.coordinator.isManualRefreshing()) {
          const transcriptionText = this.coordinator.transcriptionText();
          this.enhanceNoteText({ noteInput: `${transcriptionText} ${this.coordinator.liveTranscriptionText()}`, shouldStopLoading: false });
        }
      });
  }

  private stopPeriodicEnhancement(): void {
    this.stopEnhancement$.next();
    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    this.coordinator.setShowEnhanceLoading(false);
    this.coordinator.setIsSaving(false);
  }

  private resumeFromError(): void {
    // Clear the error flag first — autoDraftState checks hasGenerationError before status,
    // so recording would stay hidden behind the Error screen otherwise.
    this.coordinator.setHasGenerationError(false);

    // If the mic session was torn down (the normal stop→insufficient path), start recording
    // again; startVoiceTyping() reconnects to the existing autodraft and appends. If we are
    // still recording (e.g. the save-while-recording path), clearing the flag alone drops
    // us back into the InProgress state, so avoid double-starting the mic.
    const status = this.voiceTypingStatus();
    if (status !== 'recording' && status !== 'loading') {
      this.startVoiceTyping(false);
    }
  }

  public startVoiceTyping(isAutoStart: boolean): void {
    // Reset the live transcription buffer here (rather than on pause/stop) so the paused
    // overlay can keep showing the last spoken text, and it's cleared exactly once right
    // before new interim results start arriving for this session.
    this.coordinator.setLiveTranscriptionText('');
    this.liveTranscriptionControl().reset('', { emitEvent: false });

    const patientId = this.patientId();
    const siteId = this.lastAutodraftSiteId();

    // If no autodraft exists, create it first
    if (!this.activeAutodraftId() && patientId && siteId) {
      this.loadAutoDraftFromBackend({ siteId, patientId });

      // Wait for autodraft creation before starting transcription
      this.activeAutodraftId$
        .pipe(
          filter((id) => !!id),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((autodraftId) => {
          this.actuallyStartVoiceTyping(autodraftId, isAutoStart);
        });
      return;
    }

    // Autodraft already exists, start immediately
    this.actuallyStartVoiceTyping(this.coordinator.activeAutodraft().id, isAutoStart);
  }

  private actuallyStartVoiceTyping(autodraftId: string | undefined, isAutoStart: boolean): void {
    try {
      // Clear any pending stop operation
      this.clearEnhancingTimeout();

      this.store.dispatch(iosMicActions.setShouldDisplayPermissionModal({ shouldDisplay: false }));

      this.tagPusherService.pushTag({
        event: TagEvent.AutoDraftStarted,
        patient_id: this.patientId() ?? '',
        site_id: this.lastAutodraftSiteId() ?? '',
        location: isAutoStart ? 'autostart' : 'manual',
      });

      if (autodraftId && !this.isShowcaseSite()) {
        this.autoDraftGatewayService.connectToAutodraft(autodraftId);
      }

      // Set timer to hide appointment indicator after 60 seconds
      this.clearAppointmentIndicatorTimeout();
      this.appointmentIndicatorTimeout = window.setTimeout(() => {
        this.appointmentIndicatorVisible.set(false);
      }, APPOINTMENT_INDICATOR_TIMEOUT_MS);

      // The feature is declared once on the channel, so there is no context to set here. onStop's work moved
      // into stopVoiceTyping, and a failure simply closes the channel, which the watcher above finalizes.
      this.stopRequestedByUs = false;
      this.voiceChannel.open();
    } catch (error) {
      console.error('Failed to start mic recording for auto draft:', error);
      this.store.dispatch(iosMicActions.setShouldDisplayPermissionModal({ shouldDisplay: true }));
    }
  }

  public stopVoiceTyping(): void {
    const status = this.voiceTypingStatus();
    if (status !== 'recording' && status !== 'loading') {
      return;
    }

    // Close immediately to release the mic session.
    this.stopRequestedByUs = true;
    this.voiceChannel.close();
    this.handleTranscriptionResult(this.coordinator.liveTranscriptionText());
    this.beginEnhancingAfterStop();
  }

  /**
   * Runs the "wait for final transcriptions, then generate the final SOAP note" workflow that must
   * follow any stop of the active recording - whether the recording was stopped through this service's
   * own stopVoiceTyping() (pause/stop button) or externally by the voice session itself (e.g. the active
   * iOS mic disconnecting mid-recording, see the channel-went-quiet effect in the constructor). Both paths
   * need the same enhancing-state bookkeeping, otherwise showEnhanceLoading/periodic enhancement is left
   * running and the popup gets stuck showing "Updating".
   */
  private beginEnhancingAfterStop(): void {
    // Clear any existing enhancing timeout
    this.clearEnhancingTimeout();

    this.tagPusherService.pushTag({
      event: TagEvent.AutoDraftStopped,
      patient_id: this.patientId() ?? '',
      site_id: this.lastAutodraftSiteId() ?? '',
    });
    this.clearAppointmentIndicatorTimeout();

    if (this.coordinator.stopIntent() === 'pause') {
      // Pausing never enters the Enhancing state — no "Updating" spinner, and the visualizer/transcript stay mounted.
      // The note is still refreshed from the full transcript, after the same wait for the final
      // transcriptions as a stop. A patient switch also pauses, but its reset clears this timeout, so the previous
      // patient's draft gets no refresh. A resume or a stop in the wait clears it too.
      this.stopPeriodicEnhancement();

      // Without a note there is nothing to refresh. The first note appears on the next periodic pass after a resume,
      // or on a stop, and a pause must not swap the recording screen for the drafting animation.
      if (!this.coordinator.noteText().trim()) {
        return;
      }

      this.enhancingTimeout = window.setTimeout(() => {
        this.enhancingTimeout = undefined;

        const transcriptionText = `${this.coordinator.transcriptionText()} ${this.coordinator.liveTranscriptionText()}`;

        if (transcriptionText.trim()) {
          // Arm the fade only now: the overlay clears an armed fade on a paused draft while no generation runs.
          this.coordinator.requestArmSweepAnimation$.next();
          this.enhanceNoteText({
            noteInput: transcriptionText,
            shouldStopLoading: true,
            autodraftId: this.coordinator.activeAutodraft().id,
          });
        }
      }, ENHANCING_TIMEOUT_MS);
      return;
    }

    // Set enhancing state to show loading indicator while waiting for final transcriptions
    this.isEnhancing$.next(true);

    // Wait for final transcriptions to arrive through websocket before triggering enhancement
    this.enhancingTimeout = window.setTimeout(() => {
      // Keep websocket open briefly to receive trailing transcriptions from backend
      if (!this.isShowcaseSite()) {
        this.autoDraftGatewayService.leaveAutodraft();
      }

      // Read these BEFORE releasing the enhancing flag: a patient switch teardown is subscribed to it
      // and runs synchronously, clearing the coordinator before the note can be generated (FW10-3600).
      const autodraftId = this.coordinator.activeAutodraft().id;
      const coordinatorTranscription = this.coordinator.transcriptionText();
      const transcriptionText = `${coordinatorTranscription} ${this.coordinator.liveTranscriptionText()}`;

      // Reset enhancing state
      this.isEnhancing$.next(false);
      this.enhancingTimeout = undefined;

      // Trigger final SOAP note generation with all transcribed text
      // The loading state will continue until this completes (via showEnhanceLoading signal)
      // The live transcription buffer is intentionally left as-is here (cleared instead at the
      // start of the next startVoiceTyping()) so the paused overlay keeps showing the last
      // spoken text in the transcription bubble.
      if (transcriptionText.trim()) {
        this.enhanceNoteText({ noteInput: transcriptionText, shouldStopLoading: true, autodraftId });
      }
    }, ENHANCING_TIMEOUT_MS); // Wait 2 seconds for final transcriptions
  }

  public disconnectWebsocket(): void {
    if (!this.isShowcaseSite()) {
      this.autoDraftGatewayService.disconnect();
    }
  }

  private handleTranscriptionResult(transcription: string): void {
    if (!transcription.trim()) {
      return;
    }

    const currentTranscription = this.coordinator.transcriptionText();
    const newTranscription = currentTranscription ? `${currentTranscription} ${transcription}` : transcription;

    this.coordinator.setTranscriptionText(newTranscription);
    this.updateCurrentShowcaseAutodraft({ transcription: newTranscription });
  }

  public clearTranscriptionAndNote(): void {
    this.coordinator.clearActiveAutodraft();
    this.updateCurrentShowcaseAutodraft({ transcription: '', noteText: '' });
  }

  public clearAll(): void {
    this.clearTranscriptionAndNote();
    this.resetAutodraftState();
    this.clearAppointmentIndicatorTimeout();
  }

  public triggerNoteEnhancement(): void {
    const coordinatorTranscription = this.coordinator.transcriptionText();
    const transcriptionText = `${coordinatorTranscription} ${this.coordinator.liveTranscriptionText()}`;
    if (transcriptionText) {
      // While recording, the periodic enhancement owns the drafting loading flag: clearing it here would hide the
      // drafting layer and leave an empty card when the refresh returns no note (e.g. insufficient transcription).
      const shouldStopLoading = this.autoDraftState() !== AutoDraftState.InProgress;
      this.enhanceNoteText({ noteInput: transcriptionText, shouldStopLoading, isManualRefresh: true });
    } else {
      this.coordinator.setIsManualRefreshing(false);
    }
  }
  public checkForAllSoapKeywords(text: string): boolean {
    const lowerCaseText = text.toLowerCase();
    return soapKeywords.every((keyword) => lowerCaseText.includes(keyword.toLowerCase()));
  }

  private clearAppointmentIndicatorTimeout(): void {
    if (this.appointmentIndicatorTimeout) {
      window.clearTimeout(this.appointmentIndicatorTimeout);
      this.appointmentIndicatorTimeout = undefined;
    }
  }

  private clearEnhancingTimeout(): void {
    if (this.enhancingTimeout) {
      window.clearTimeout(this.enhancingTimeout);
      this.enhancingTimeout = undefined;
      this.isEnhancing$.next(false);
    }
  }

  public openConfirmDeleteDialog(): void {
    const deleteDialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[480px]'],
      disableClose: true,
      data: {
        destructive: true,
        title: 'auto_draft_delete_title',
        description: 'auto_draft_delete_description',
        cancelLabel: 'auto_draft_delete_cancel',
        confirmLabel: 'auto_draft_delete_confirm',
      },
    });

    deleteDialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result?.confirm) {
        return;
      }
      this.deleteAutodraftFromBackend();
    });
  }

  public openConfirmStartNewDialog(): void {
    const dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[360px]'],
      disableClose: true,
      data: {
        iconKey: 'px-40-autodraft-info',
        title: 'auto_draft_start_new_title',
        description: 'auto_draft_start_new_description',
        subtext: 'auto_draft_start_new_note',
        cancelLabel: 'auto_draft_start_new_cancel',
        confirmLabel: 'auto_draft_start_new_confirm',
      },
    });

    dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result?.confirm) {
        return;
      }
      this.deleteAutodraftAndStartNew();
    });
  }

  private deleteAutodraftAndStartNew(): void {
    const siteId = this.lastAutodraftSiteId();
    const patientId = this.patientId();
    const autodraftId = this.coordinator.activeAutodraft().id;

    // Showcase drafts live in memory only, so they are dropped locally and never DELETEd. This has to run
    // before clearAll(), which wipes the coordinator's active id that identifies the draft to delete; it
    // also clears that id itself, so the clearAll() below cannot re-create the entry it just removed.
    // Recording is deliberately NOT restarted here: it would skip the consent gate, and the backend path
    // below only resets and waits for the user to press Start.
    if (this.isShowcaseSite()) {
      this.showcaseAutodraftService.deleteCurrentAutodraft();
      this.clearAll();
      this.coordinator.setIsReadOnly(false);
      this.coordinator.setActiveAutodraftId();
      return;
    }

    this.clearAll();

    if (siteId && patientId && autodraftId) {
      this.autoDraftDataService.deleteAutodraft(siteId, autodraftId).subscribe({
        next: () => {
          this.notesScreenService.getVisits({ shouldShowLoading: false });
          this.coordinator.setIsReadOnly(false);
          this.coordinator.setActiveAutodraftId();
        },
        error: (error) => {
          console.error('Error deleting autoDraft for start new:', error);
          this.toastService.error(this.translateService.instant('auto_draft_delete_error_message'));
        },
      });
    }
  }

  public deleteAutodraftFromBackend(): void {
    const siteId = this.lastAutodraftSiteId();
    const patientId = this.patientId();
    const isSelectedDelete = !!this.coordinator.selectedAutodraft();
    const autodraftId = this.coordinator.displayedAutodraft().id;
    const wasRecording = this.isRecordingActive();

    if (!isSelectedDelete && wasRecording) {
      this.clearTranscriptionDuringRecording();
      this.toastService.success(this.translateService.instant('auto_draft_delete_success_message'));
      return;
    }

    // Showcase drafts live in memory only - deleting them locally is the whole operation, and issuing the
    // backend DELETE would just 404 into a global "Not found" toast (FW10-4162).
    if (this.isShowcaseSite()) {
      if (isSelectedDelete) {
        // A historical delete must not touch the active session - a recording may be running.
        this.showcaseAutodraftService.deleteAutodraftById(autodraftId);
        this.coordinator.clearSelectedAutodraft();
        this.coordinator.setIsDrawerOpen(false);
      } else {
        // deleteCurrentAutodraft() clears the coordinator's active id first, so clearAll() below cannot
        // re-create the entry through updateCurrentShowcaseAutodraft().
        this.showcaseAutodraftService.deleteCurrentAutodraft();
        this.clearAll();
        this.coordinator.setIsReadOnly(false);
        this.coordinator.setActiveAutodraftId();
      }
      this.toastService.success(this.translateService.instant('auto_draft_delete_success_message'));
      return;
    }

    if (siteId && patientId && autodraftId) {
      this.autoDraftDataService.deleteAutodraft(siteId, autodraftId).subscribe({
        next: () => {
          if (isSelectedDelete) {
            // A historical delete must not touch the active session — a recording may be running.
            this.coordinator.clearSelectedAutodraft();
            this.coordinator.setIsDrawerOpen(false);
            this.toastService.success(this.translateService.instant('auto_draft_delete_success_message'));
            this.notesScreenService.getVisits({ shouldShowLoading: false });
          } else {
            this.clearAll();
            this.coordinator.setIsReadOnly(false);
            this.coordinator.setActiveAutodraftId();
            this.toastService.success(this.translateService.instant('auto_draft_delete_success_message'));
            this.notesScreenService.getVisits({ shouldShowLoading: false });
          }
        },
        error: (error) => {
          console.error('Error deleting autoDraft:', error);
          this.toastService.error(this.translateService.instant('auto_draft_delete_error_message'));
        },
      });
    }
  }

  private generateFinalNote(): void {
    const transcription = this.coordinator.transcriptionText();

    if (transcription.trim()) {
      this.enhanceNoteText({ noteInput: transcription, shouldStopLoading: true, autodraftId: this.coordinator.activeAutodraft().id });
    }
  }

  /**
   * The Stop button and AutoStop end the draft for the user: they stop the recording, then save the final note to the
   * PMS, which also closes the overlay and navigates to the patient's notes. With no note to save
   * (not enough clinical content, or a failed generation) nothing is saved, and the overlay, opened by the stop, shows why.
   */
  private stopAndSaveNote(): void {
    this.coordinator.commandStopVoiceTyping$.next();

    this.autoDraftState$
      .pipe(
        filter((state) => state === AutoDraftState.Stopped || state === AutoDraftState.Error),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        const noteText = this.coordinator.noteText();

        if (state === AutoDraftState.Stopped && noteText.trim()) {
          this.saveNoteAsClinicalNote(noteText);
        }
      });
  }

  private isRecordingActive(): boolean {
    const status = this.voiceTypingStatus();

    return status === 'recording' || status === 'loading';
  }

  /**
   * A delete during recording drops what was said so far, but the dictation goes on into the same draft. The draft is
   * not deleted: that left the websocket room, and the transcript pushed after it never reached the backend, so every
   * later generation failed on an insufficient transcript. Instead the backend transcript is cleared with an empty
   * transcription update, and the pushes after the delete build it up again.
   */
  private clearTranscriptionDuringRecording(): void {
    const autodraftId = this.coordinator.activeAutodraft().id;

    this.coordinator.setTranscriptionText('');
    this.coordinator.setNoteText('');
    this.coordinator.setLiveTranscriptionText('');
    this.liveTranscriptionControl().reset('', { emitEvent: false });
    this.coordinator.setHasGenerationError(false);
    this.updateCurrentShowcaseAutodraft({ transcription: '', noteText: '' });
    // Aborts a generation still running on the old transcript and restarts the periodic one on the empty transcript.
    this.startPeriodicEnhancement();

    if (autodraftId && !this.isShowcaseSite()) {
      this.autoDraftGatewayService.updateTranscription('', autodraftId);
    }
  }

  public readonly enhanceNoteText = this.effect(
    ($: Observable<{ noteInput: string; shouldStopLoading?: boolean; isManualRefresh?: boolean; autodraftId?: string }>) =>
      $.pipe(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        switchMap(({ noteInput, shouldStopLoading = false, isManualRefresh = false, autodraftId }) => {
          if (!noteInput.trim()) {
            if (isManualRefresh) {
              this.coordinator.setIsManualRefreshing(false);
            }
            return EMPTY;
          }

          // A stop's generation outlives its session, so pin the target instead of re-reading it later.
          const targetAutodraftId = autodraftId ?? this.coordinator.activeAutodraft().id;
          // Gates content writes so a previous patient's note can't surface under the current one. Not
          // showEnhanceLoading: that flag also stops the periodic-enhancement cleanup aborting this request.
          const withSession = (mutate: () => void): void => {
            if (this.coordinator.activeAutodraft().id === targetAutodraftId) {
              mutate();
            }
          };

          if (!this.abortController.signal.aborted) {
            this.abortController.abort();
          }
          this.abortController = new AbortController();

          withSession(() => this.coordinator.setHasGenerationError(false));
          this.coordinator.setShowEnhanceLoading(true);
          this.enhancedTextAccumulator = '';

          if (this.isShowcaseSite()) {
            this.showcaseAutodraftService.enhanceShowcaseAutodraftNote({
              noteInput,
              signal: this.abortController.signal,
              autodraftId: targetAutodraftId,
              fallbackInitiatorId: this.userId() ?? '',
              shouldStopLoading,
              setLoading: (isLoading) => this.coordinator.setShowEnhanceLoading(isLoading),
            });

            if (isManualRefresh) {
              this.coordinator.setIsManualRefreshing(false);
            }
            return EMPTY;
          }

          return this.tokenService.getAccessToken$({ isUserActivity: true }).pipe(
            switchMap((token) => {
              const siteId = this.lastAutodraftSiteId();

              if (!siteId || !targetAutodraftId) {
                console.error('Missing siteId or autodraftId for SOAP generation', siteId, targetAutodraftId);
                this.coordinator.setShowEnhanceLoading(false);
                return EMPTY;
              }

              const visitDate = findTodayVisit(this.notesScreenService.state().visits, this.notesScreenService.timezoneSignal() as string)
                ?.visitDate;

              return from(
                fetch(
                  `${this.configService.config()?.apiBasePath}/v1/sites/${siteId}/autodrafts/${targetAutodraftId}/soap/generate-stream`,
                  {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ visitDate }),
                    signal: this.abortController.signal,
                  },
                ),
              );
            }),
            catchError((error) => {
              if (error.name === 'AbortError') {
                // AbortError is expected when stopping enhancement
                this.coordinator.setShowEnhanceLoading(false);
              } else {
                console.error('Error while enhancing auto-draft note:', error);
                if (shouldStopLoading) {
                  withSession(() => this.coordinator.setHasGenerationError(true));
                  this.coordinator.setShowEnhanceLoading(false);
                }
                //this.toastService.error(this.translateService.instant('notes_enhance_error_toast_message'));
              }
              return EMPTY;
            }),
            switchMap((response) => {
              if (!response.ok) {
                if (shouldStopLoading) {
                  this.coordinator.setShowEnhanceLoading(false);
                }
                return from(response.json()).pipe(
                  tap((responseJson) => {
                    console.error('Enhancement failed with response:', responseJson);
                    // Insufficient content must resolve to the empty Stopped overlay, not Error, and without a toast
                    // — so leave hasGenerationError unset. The error flag is only set once the code is known.
                    if (responseJson?.code === ErrorCode.INSUFFICIENT_TRANSCRIPTION) {
                      return;
                    }
                    if (shouldStopLoading) {
                      withSession(() => this.coordinator.setHasGenerationError(true));
                    }
                    if (responseJson?.message) {
                      this.toastService.error(responseJson.message);
                    }
                  }),
                  catchError((parseError) => {
                    console.error('Failed to parse enhancement error response:', parseError);
                    if (shouldStopLoading) {
                      withSession(() => this.coordinator.setHasGenerationError(true));
                    }
                    return EMPTY;
                  }),
                  switchMap(() => EMPTY),
                );
              }

              return response.body
                ? fromReadableStream(response.body, {
                    ngZone: this.ngZone,
                    signal: this.abortController.signal,
                  }).pipe(
                    tap({
                      next: (chunk) => {
                        const encoder = new TextDecoder();
                        const text = encoder.decode(chunk);
                        this.enhancedTextAccumulator += text;

                        // Show the note only once all SOAP sections have started; it keeps growing with each chunk.
                        const hasAllSoapKeywords = this.checkForAllSoapKeywords(this.enhancedTextAccumulator);
                        if (hasAllSoapKeywords) {
                          withSession(() => this.coordinator.setNoteText(this.enhancedTextAccumulator));
                        }
                      },
                      // The loading ends only with the stream: the last section is still streaming once all SOAP
                      // sections have started, and a save that waits for the loading to end would cut off its end.
                      complete: () => {
                        if (shouldStopLoading) {
                          this.coordinator.setShowEnhanceLoading(false);
                        }
                      },
                      error: (error) => {
                        if (error.name === 'AbortError') {
                          // AbortError is expected when stopping enhancement
                          this.coordinator.setShowEnhanceLoading(false);
                        } else {
                          console.error('Error during note enhancement stream:', error);
                          if (shouldStopLoading) {
                            withSession(() => this.coordinator.setHasGenerationError(true));
                            this.coordinator.setShowEnhanceLoading(false);
                          }
                        }
                      },
                    }),
                  )
                : EMPTY;
            }),
            finalize(() => {
              if (isManualRefresh) {
                this.coordinator.setIsManualRefreshing(false);
              }
            }),
          );
        }),
      ),
  );

  /**
   * Enhance the note and save it as a clinical note once enhancement is complete.
   * This is used when saving during an active recording session.
   */
  public enhanceAndSaveNote(): void {
    this.coordinator.setIsSaving(true);
    const coordinatorTranscription = this.coordinator.transcriptionText();
    const transcriptionText = `${coordinatorTranscription} ${this.coordinator.liveTranscriptionText()}`;
    if (!transcriptionText) {
      this.toastService.error(this.translateService.instant('auto_draft_save_error_missing_data'));
      return;
    }

    // Trigger enhancement
    this.enhanceNoteText({ noteInput: transcriptionText, shouldStopLoading: true });

    // Wait for enhancement to complete, then save
    this.showEnhanceLoading$
      .pipe(
        filter((isLoading) => !isLoading),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const enhancedText = this.coordinator.noteText();
        if (enhancedText) {
          this.saveNoteAsClinicalNote(enhancedText);
        }
      });
  }

  /**
   * Regenerate the note from everything said so far and call `done` once the stream has finished, so a consumer
   * (inserting the draft into a clinical note) gets the up-to-date SOAP. Calls `done` right away with nothing to
   * regenerate from; a failed generation also ends the loading, so `done` then sees the previous note.
   */
  private refreshNoteThen(done: () => void): void {
    const transcriptionText = `${this.coordinator.transcriptionText()} ${this.coordinator.liveTranscriptionText()}`;
    if (!transcriptionText.trim()) {
      done();
      return;
    }

    this.enhanceNoteText({ noteInput: transcriptionText, shouldStopLoading: true });

    this.showEnhanceLoading$
      .pipe(
        filter((isLoading) => !isLoading),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(done);
  }

  public readonly saveNoteAsClinicalNote = this.effect(($: Observable<string>) =>
    $.pipe(
      switchMap((noteText) => {
        const siteId = this.lastAutodraftSiteId();
        const patientId = this.patientId();

        if (!noteText?.trim() || !siteId || !patientId) {
          this.coordinator.setIsSaving(false);
          this.toastService.error(this.translateService.instant('auto_draft_save_error_missing_data'));
          return EMPTY;
        }

        this.coordinator.setIsSaving(true);
        this.notesScreenService.patchState({ noteSavingStatus: DataState.Loading });

        return iif(
          () => this.showcaseAutodraftService.isShowcaseSite(siteId),
          of({ clinicalNoteId: crypto.randomUUID() }),
          this.clinicalNotesDataService.addClinicalNoteBySiteV2(siteId, patientId, {
            noteText: noteText,
            visitId: undefined,
            providerId: this.noteProviderForNewNote()?.id ?? '',
          }),
        ).pipe(
          switchMap((response) => {
            const createdClinicalNoteId = response.clinicalNoteId;

            // In showcase mode, pre-populate the UI similar to notes screen service
            if (this.showcaseAutodraftService.isShowcaseSite(siteId)) {
              const now = new Date();
              const clinicalNote = this.createClinicalNote(createdClinicalNoteId, now, patientId, noteText);

              const visits = this.notesScreenService.showcaseVisitsCache.get(patientId) ?? [];
              const newVisit = visits.find((visit) => visit.visitDate === DateTime.now().toISODate());

              if (newVisit) {
                this.updateSelectedVisit(newVisit, visits, clinicalNote);
              } else {
                this.createNewVisit(now, siteId, patientId, clinicalNote);
              }

              this.finalizeNoteSave(newVisit?.visitId ?? NEW_VISIT_ID, createdClinicalNoteId, patientId, siteId);
              return of(true);
            }

            // For non-showcase mode, fetch visits and handle as before
            this.notesScreenService.getVisits({ shouldShowLoading: true });

            return this.notesScreenService.state$.pipe(
              filter((state) => state.listStatus === DataState.Fetched),
              take(1),
              tap(() => {
                const todayVisit = findTodayVisit(
                  this.notesScreenService.state().visits,
                  this.notesScreenService.timezoneSignal() as string,
                );
                this.notesScreenService.patchState({ activeVisit: todayVisit });
                this.finalizeNoteSave(todayVisit?.visitId, createdClinicalNoteId, patientId, siteId);
              }),
            );
          }),
          catchError((error: ApiHttpErrorResponse) => {
            console.error('Error saving auto-draft note:', error);
            const messageKey =
              error.error?.code === ErrorCode.EAGLESOFT_CREDENTIALS_INVALID
                ? 'notes_eaglesoft_credentials_invalid_error_message'
                : 'auto_draft_save_error_message';
            this.toastService.error(this.translateService.instant(messageKey));
            this.coordinator.setIsSaving(false);
            return EMPTY;
          }),
        );
      }),
    ),
  );
}
